// src/components/RoomHeroGallery/RoomHeroGallery.jsx
import React, { useState, useCallback, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';

// Importa os componentes e estilos do Swiper para o Lightbox
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, A11y } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import './RoomHeroGallery.css';

const RoomHeroGallery = ({ images, title }) => {
  const [currentIndex, setCurrentImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [initialSlide, setInitialSlide] = useState(0);

  const MAX_THUMBNAILS = 3;
  const hasMoreImages = images && images.length > MAX_THUMBNAILS;
  const remainingImagesCount = hasMoreImages ? images.length - MAX_THUMBNAILS : 0;
  const visibleThumbnails = images ? images.slice(0, MAX_THUMBNAILS) : [];

  const openLightbox = useCallback((index = 0) => {
    setInitialSlide(index); // Define o slide inicial para o Swiper
    setIsLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  }, []);

  const closeLightbox = useCallback(() => {
    setIsLightboxOpen(false);
    document.body.style.overflow = 'auto';
  }, []);

  // Funções para navegar na galeria principal (fora do lightbox)
  const goToNext = useCallback(() => {
    if (!images) return;
    setCurrentImageIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images]);

  const goToPrev = useCallback(() => {
    if (!images) return;
    setCurrentImageIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images]);

  const selectImage = (index) => {
    setCurrentImageIndex(index);
  };

  // Efeito para fechar o lightbox com a tecla 'Esc'
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (isLightboxOpen && e.key === 'Escape') {
        closeLightbox();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isLightboxOpen, closeLightbox]);

  if (!Array.isArray(images) || images.length === 0) {
    return <div className="room-hero-gallery-placeholder"><span>Sem imagens disponíveis</span></div>;
  }

  return (
    <>
      <div className="room-hero-gallery">
        <div className="main-image-wrapper">
          <img key={images[currentIndex]} src={images[currentIndex]} alt={`${title} - Foto ${currentIndex + 1}`} className="main-image" onClick={() => openLightbox(currentIndex)} />
          {images.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); goToPrev(); }} className="gallery-nav-btn prev"><FaChevronLeft /></button>
              <button onClick={(e) => { e.stopPropagation(); goToNext(); }} className="gallery-nav-btn next"><FaChevronRight /></button>
            </>
          )}
        </div>

        {images.length > 1 && (
          <div className="thumbnail-wrapper">
            {visibleThumbnails.map((image, index) => {
              const isLastVisible = index === MAX_THUMBNAILS - 1;
              const shouldShowMoreIndicator = isLastVisible && hasMoreImages;
              return (
                <div
                  key={index}
                  className={`thumbnail-item ${index === currentIndex ? 'active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); shouldShowMoreIndicator ? openLightbox(index) : selectImage(index); }}
                >
                  <img src={image} alt={`Miniatura ${index + 1}`} />
                  {shouldShowMoreIndicator && (
                    <div className="thumbnail-more-overlay">
                      <span>+{remainingImagesCount}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {isLightboxOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}><FaTimes /></button>

            {/* --- LIGHTBOX COM SWIPER --- */}
            <div className="lightbox-swiper-container">
              <Swiper
                modules={[Navigation, Pagination, A11y]}
                navigation
                pagination={{ type: 'fraction' }} // Mostra "1 / 10"
                loop={true}
                initialSlide={initialSlide} // Começa no slide que o usuário clicou
                className="lightbox-swiper"
              >
                {images.map((img, index) => (
                  <SwiperSlide key={index}>
                    <img src={img} alt={`${title} - Foto ${index + 1}`} className="lightbox-image" />
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RoomHeroGallery;